import { create } from "zustand";
import {
  api,
  ApiError,
  type ClientMap,
  type ClientMapInfo,
  type PrivateMap,
  type ShareMode,
} from "@/lib/api";

interface MapStore {
  maps: ClientMapInfo[];
  currentMap: ClientMap | null;
  currentSecret: string | null;
  loading: boolean;
  error: string | null;
  errorCode: number | null;

  fetchMaps: () => Promise<void>;
  createMap: (name: string) => Promise<PrivateMap>;
  deleteMap: (id: string, adminId: string) => Promise<void>;
  duplicateMap: (id: string) => Promise<void>;
  loadMap: (id: string, secret?: string) => Promise<ClientMap>;
  setCurrentSecret: (secret: string | null) => void;
  toggleVisibility: (mapId: string, adminId: string) => Promise<void>;
  updateShareMode: (mapId: string, adminId: string, mode: ShareMode, collaboratorEmails?: string[]) => Promise<void>;
  clearCurrentMap: () => void;
  clearError: () => void;
}

export const useMapStore = create<MapStore>((set, get) => ({
  maps: [],
  currentMap: null,
  currentSecret: null,
  loading: false,
  error: null,
  errorCode: null,

  fetchMaps: async () => {
    try {
      const res = await api.listMaps();
      set({ maps: res ?? [] });
    } catch {
      // Silently fail — don't overwrite loadMap error state
    }
  },

  createMap: async (name: string) => {
    set({ loading: true, error: null });
    try {
      const res = await api.createMap({
        rootNode: {
          name,
          colors: { name: "#ffffff", background: "#2563eb", branch: "#3b82f6" },
          font: { style: "normal", size: 16, weight: "bold" },
          image: { src: "", size: null },
        },
      });
      await get().fetchMaps();
      return res;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to create map" });
      throw e;
    } finally {
      set({ loading: false });
    }
  },

  deleteMap: async (id: string, adminId: string) => {
    set({ error: null });
    try {
      await api.deleteMap(id, adminId);
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to delete map" });
      throw e;
    }
    // Re-fetch to ensure consistency
    await get().fetchMaps();
  },

  duplicateMap: async (id: string) => {
    set({ error: null });
    try {
      await api.duplicateMap(id);
      await get().fetchMaps();
    } catch (e) {
      set({
        error: e instanceof Error ? e.message : "Failed to duplicate map",
      });
      throw e;
    }
  },

  loadMap: async (id: string, secret?: string) => {
    set({ loading: true, error: null, errorCode: null, currentMap: null, currentSecret: secret ?? null });
    try {
      const res = await api.getMap(id, secret);
      set({ currentMap: res });
      return res;
    } catch (e) {
      const code = e instanceof ApiError ? e.status
        : e != null && typeof (e as Record<string, unknown>).status === "number" ? (e as Record<string, unknown>).status as number
        : null;
      set({
        error: e instanceof Error ? e.message : "Failed to load map",
        errorCode: code,
      });
      return undefined as unknown as ClientMap;
    } finally {
      set({ loading: false });
    }
  },

  toggleVisibility: async (mapId: string, adminId: string) => {
    const current = get().currentMap;
    const newPublic = !(current?.public ?? false);
    console.log(`[Visibility] toggling map=${mapId} adminId=${adminId} newPublic=${newPublic}`);
    try {
      await api.setMapVisibility(mapId, adminId, newPublic);
      if (current) {
        set({ currentMap: { ...current, public: newPublic } });
      }
      await get().fetchMaps();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to toggle visibility" });
    }
  },

  updateShareMode: async (mapId: string, adminId: string, mode: ShareMode, collaboratorEmails?: string[]) => {
    const current = get().currentMap;
    try {
      const settings = await api.updateMapShareSettings(mapId, adminId, mode, collaboratorEmails);
      if (current) {
        set({ currentMap: { ...current, public: settings.public } });
      }
      await get().fetchMaps();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to update share settings" });
      throw e;
    }
  },

  setCurrentSecret: (secret) => set({ currentSecret: secret }),
  clearCurrentMap: () => set({ currentMap: null, currentSecret: null }),
  clearError: () => set({ error: null, errorCode: null }),
}));
